import {
  createContext,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import { ClickAwayListener, useTheme } from '@mui/material';
import reduce from 'lodash/reduce';
import { nanoid } from 'nanoid';

import { InferComponentProps } from '@cast/shared-types';

import { drawerInitialState, drawerReducer } from './drawerReducer';
import { DrawerReducerAction, DrawerReducerState, DrawerState } from './types';
import { findChildrenDrawerIds, findParentDrawerIds } from './utils';
import { DrawerProps, OpenDrawerArgs, ValidDrawerComponent } from '../types';

export type DrawerInstance<T extends DrawerProps = DrawerProps> = {
  id: string;
  update: (id: string, props: Partial<T>) => void;
  destroy: (id: string) => void;
};

export type DrawerCloseOptions = {
  /**
   * Closes all currently open drawers, including child drawers
   */
  all?: boolean;
  /**
   * Forces the drawer to close even if a close guard is active
   * @default true
   */
  force?: boolean;
  /**
   * Callback to be called after the drawer is closed and all transitions are complete
   */
  onComplete?: () => void;
};

type DrawerContextState = {
  open: <
    COMPONENT extends ValidDrawerComponent,
    PROPS = InferComponentProps<COMPONENT>
  >(
    args: OpenDrawerArgs<COMPONENT, PROPS>
  ) => DrawerInstance<DrawerProps & PROPS>;
  drawers: DrawerReducerState['drawers'];
};

export const DrawerContext = createContext<DrawerContextState | undefined>(
  undefined
);

export const DrawerInstanceContext = createContext<
  DrawerState & {
    close: (options?: DrawerCloseOptions) => void;
    shouldRenderGuard: boolean;
    setShouldRenderGuard: (guard: boolean) => void;
    setOnCloseGuard: (guard: boolean) => void;
    isInDrawer: boolean;
  }
>({ isInDrawer: false } as never);

type RenderDrawerProps = {
  id: string;
  state: DrawerReducerState;
  dispatch: Dispatch<DrawerReducerAction>;
};

const getTranslateX = (element: Element) => {
  const style = window.getComputedStyle(element);
  const matrix = new WebKitCSSMatrix(style.transform);
  return matrix.m41 > 0 ? matrix.m41 : 0;
};

// Modals, popovers and poppers (e.g. dropdown menus) render in portals outside
// the drawer's DOM subtree, so clicks inside them must not count as "outside".
const isClickInsidePortal = (e: Event) =>
  e
    .composedPath()
    .some(
      (target) =>
        target instanceof Element &&
        (target.classList.contains('MuiModal-root') ||
          target.classList.contains('MuiPopover-root') ||
          target.classList.contains('MuiPopper-root'))
    );

const hasClickedDrawer = (e: Event, _id: string) => {
  const nestedDrawer = document.getElementById(_id);
  if (nestedDrawer && e.target instanceof Node) {
    const hasTriggerHappenedWithinNestedDrawer = e
      .composedPath()
      .includes(nestedDrawer);

    return isClickInsidePortal(e) || hasTriggerHappenedWithinNestedDrawer;
  }

  return false;
};

const RenderDrawer = ({ id, state, dispatch }: RenderDrawerProps) => {
  const theme = useTheme();
  const [ref, setRef] = useState<HTMLDivElement | undefined>();
  const [shouldRenderGuard, setShouldRenderGuard] = useState<boolean>(false);
  const [onCloseGuard, setOnCloseGuard] = useState<boolean>(false);
  const drawerTranslateX = useRef<number>(0);

  const parentDrawerIds = findParentDrawerIds(id, Object.values(state.drawers));
  const childrenDrawerIds = findChildrenDrawerIds(
    id,
    Object.values(state.drawers)
  );

  const drawerState = state.drawers[id];
  const {
    component: Component,
    props: componentProps,
    parentId,
    translateX,
  } = drawerState;
  const parent = parentId ? state.drawers[parentId] : undefined;

  const closeSingleDrawer = (onComplete?: DrawerCloseOptions['onComplete']) => {
    // initiates drawer transition
    dispatch({
      type: 'update',
      payload: {
        id,
        props: {
          open: false,
        },
      },
    });

    // initiates parent drawer transition
    if (parentId) {
      const siblingDrawerIds = reduce(
        state.drawers,
        (acc, drawer) => {
          if (drawer.parentId === parentId && drawer.id !== id) {
            acc.push(drawer.id);
          }
          return acc;
        },
        [] as string[]
      );

      if (!siblingDrawerIds.length) {
        dispatch({
          type: 'update',
          payload: {
            id: parentId,
            translateX: -drawerTranslateX.current,
          },
        });
      }
    }

    // waits for transition to end and removes drawer from state
    setTimeout(() => {
      dispatch({ type: 'destroy', payload: { id } });
      onComplete?.();
    }, theme.transitions.duration.leavingScreen);
  };

  const closeAllDrawers = (onComplete?: DrawerCloseOptions['onComplete']) => {
    Object.keys(state.drawers).forEach((id) => {
      dispatch({
        type: 'update',
        payload: {
          id,
          props: { open: false },
          translateX: 0,
        },
      });
    });

    // waits for transition to end and removes drawer from state
    setTimeout(() => {
      dispatch({ type: 'destroy', payload: { id } });
      onComplete?.();
    }, theme.transitions.duration.leavingScreen);
  };

  const handleClose = (
    event: object, // Must match MUI type
    reason: 'backdropClick' | 'escapeKeyDown',
    { all, force = true, onComplete }: DrawerCloseOptions = {}
  ) => {
    if (!onCloseGuard || force) {
      setShouldRenderGuard(false);
      if (all) {
        closeAllDrawers(onComplete);
      } else {
        closeSingleDrawer(onComplete);
      }
    } else {
      setShouldRenderGuard(true);
    }
    componentProps?.onClose?.(event, reason);
  };

  const handleClickAway = (e: Event) => {
    if (componentProps?.disableCloseOnBackdropClick) {
      return;
    }

    if (e.target instanceof Node) {
      const isChildGuardActive = childrenDrawerIds.some(
        (childId) => state.drawers[childId].closeGuardIsActive
      );
      if (isChildGuardActive) {
        return;
      }

      // Do not close the drawer when the click originated inside a portal
      // opened from within the drawer (e.g. a Dialog, Menu or dropdown).
      if (isClickInsidePortal(e)) {
        return;
      }

      const hasClickedDrawerFn = hasClickedDrawer.bind(null, e);
      const hasClickedChild = childrenDrawerIds.some(hasClickedDrawerFn);
      if (hasClickedChild) {
        return;
      }

      const hasClickedParent = parentDrawerIds.some(hasClickedDrawerFn);
      handleClose(e, 'backdropClick', {
        all: !(hasClickedParent || state.order.length === 1),
        force: false,
      });
    }
  };

  // opening with open false for animation to be present
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch({
        type: 'update',
        payload: {
          id,
          props: { open: true, className: 'DS-Drawer-no-backdrop' },
        },
      });
    }, theme.transitions.duration.enteringScreen);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [dispatch, id, theme.transitions.duration.enteringScreen]);

  // memorize child ref for translateX calculation
  useEffect(() => {
    if (parentId && parent && ref) {
      drawerTranslateX.current = getTranslateX(
        ref.querySelector('.MuiDrawer-paper')!
      );

      dispatch({
        type: 'update',
        payload: {
          id: parentId,
          translateX: drawerTranslateX.current,
          initialTranslateX: drawerTranslateX.current,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, parentId, ref]);

  useEffect(() => {
    let transform = 'none';

    if (translateX && translateX > 0) {
      transform = `translateX(-${translateX}px)`;
    }
    dispatch({
      type: 'update',
      payload: {
        id,
        props: {
          open: true,
          PaperProps: {
            style: { transform },
          },
        },
      },
    });
  }, [dispatch, id, translateX]);

  useEffect(() => {
    if (!onCloseGuard) {
      setShouldRenderGuard(false);
    }
  }, [onCloseGuard]);

  useEffect(() => {
    dispatch({
      type: 'update',
      payload: {
        id,
        closeGuardIsActive: onCloseGuard,
      },
    });
  }, [dispatch, id, onCloseGuard, shouldRenderGuard]);

  return (
    <DrawerInstanceContext.Provider
      value={{
        ...drawerState,
        shouldRenderGuard,
        setShouldRenderGuard,
        setOnCloseGuard,
        close: (options) => {
          // This is a manually triggered close, so we simulate an event and reason.
          // We could avoid this by not calling the drawer's onClose callback,
          // but I'm worried it may break some use cases where the consumer relies on that callback
          // even when close is called programmatically.
          handleClose({}, 'backdropClick', options);
        },
        isInDrawer: true,
      }}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Component
          {...componentProps}
          ref={(_ref: HTMLDivElement) => {
            setRef(_ref);
          }}
          id={id}
          onClose={(event, reason) => {
            handleClose(event, reason, { force: false });
          }}
        />
      </ClickAwayListener>
    </DrawerInstanceContext.Provider>
  );
};

export const DrawerProvider = ({
  children,
  wrapper,
}: PropsWithChildren<{ wrapper?: (children: ReactNode) => ReactNode }>) => {
  const [state, dispatch] = useReducer(drawerReducer, drawerInitialState);

  const update = useCallback((id: string, props: DrawerProps) => {
    dispatch({
      type: 'update',
      payload: {
        id,
        props,
      },
    });
  }, []);

  const destroy = useCallback((id: string) => {
    dispatch({
      type: 'destroy',
      payload: {
        id,
      },
    });
  }, []);

  const open = <
    COMPONENT extends ValidDrawerComponent,
    PROPS = InferComponentProps<COMPONENT>
  >({
    component,
    props,
    options,
  }: OpenDrawerArgs<COMPONENT, DrawerProps & PROPS>) => {
    let id: string;
    const visibleDrawer: DrawerReducerState['drawers'][number] | undefined =
      Object.values(state.drawers).find(({ component: c }) => c === component);

    if (visibleDrawer?.closeGuardIsActive) {
      id = visibleDrawer.id;
    } else {
      id = nanoid();

      dispatch({
        type: 'open',
        payload: {
          id,
          component,
          props,
          parentId: options?.parentId,
        },
      });
    }

    return {
      id,
      update,
      destroy,
    };
  };

  return (
    <DrawerContext.Provider
      value={{
        drawers: state.drawers,
        open,
      }}
    >
      {children}

      {wrapper
        ? wrapper(
            <>
              {state.order.map((id) => (
                <RenderDrawer
                  key={id}
                  id={id}
                  state={state}
                  dispatch={dispatch}
                />
              ))}
            </>
          )
        : state.order.map((id) => (
            <RenderDrawer key={id} id={id} state={state} dispatch={dispatch} />
          ))}
    </DrawerContext.Provider>
  );
};
